import { Component } from "@theme/component";
import { VariantSelectedEvent, VariantUpdateEvent } from "@theme/events";
import { morph } from "@theme/morph";
import { requestYieldCallback } from "@theme/utilities";

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [Refs = {}]
 *
 * @extends Component<Refs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {Array<{id: string, available: boolean, option1: string|null, option2: string|null, option3: string|null}>} */
  #allVariants = [];

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener("change", this.#onChange);
    this.#parseAllVariants();
  }

  #onChange = (event) => this.variantChanged(event);

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener("change", this.#onChange);
    this.#abortController?.abort();
  }

  #parseAllVariants() {
    try {
      const data = JSON.parse(
        this.querySelector('script[type="application/json"]')?.textContent ??
          "[]",
      );
      this.#allVariants = Array.isArray(data) ? data : [];
    } catch {
      this.#allVariants = [];
    }
  }

  #findCurrentVariant() {
    const selectedOptions = Array.from(
      this.querySelectorAll(
        'select:not([data-auxiliary-select="true"]) option[selected], fieldset input:checked',
      ),
    );
    const textValues = selectedOptions.map((el) => el.value);
    if (!textValues.length) return undefined;

    return this.#allVariants.find((v) => {
      const vOptions = [v.option1, v.option2, v.option3].filter(Boolean);
      return textValues.every((val, i) => vOptions[i] === val);
    });
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const effectiveTarget = this.#getEffectiveTarget(event.target);
    if (!effectiveTarget) return;

    const selectedOption =
      effectiveTarget instanceof HTMLSelectElement
        ? effectiveTarget.options[effectiveTarget.selectedIndex]
        : effectiveTarget;

    if (!selectedOption) return;

    if (event.target instanceof HTMLSelectElement) {
      this.updateSelectedOption(event.target);
    }

    this.updateSelectedOption(effectiveTarget);
    this.dispatchEvent(
      new VariantSelectedEvent({
        id: selectedOption.dataset.optionValueId ?? "",
      }),
    );

    const isOnProductPage =
      !this.closest('[data-purchase-context="embedded"]') &&
      this.dataset.templateProductMatch === "true" &&
      !event.target.closest("product-card") &&
      !event.target.closest("quick-add-dialog");

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split("?")[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct =
      isOnProductPage && !!newUrl && newUrl !== currentUrl;

    // For non-combined-listing pages, fire VariantUpdateEvent immediately from
    // inline variant data so the gallery, button, and inventory update without
    // waiting for the Section Rendering fetch.
    if (!loadsNewProduct) {
      const inlineVariant = this.#findCurrentVariant();
      if (inlineVariant) {
        this.dispatchEvent(
          new VariantUpdateEvent(inlineVariant, this.selectedOptionId, {
            html: null,
            productId: this.dataset.productId ?? "",
          }),
        );
      }
    }

    this.fetchUpdatedSection(
      this.buildRequestUrl(selectedOption),
      loadsNewProduct,
    );

    const url = new URL(window.location.href);

    const variantId = selectedOption.dataset.variantId || null;

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set("variant", variantId);
      } else {
        url.searchParams.delete("variant");
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, "", url.toString());
      });
    }
  }

  /**
   * Resolves proxy controls to the canonical input used by the variant picker.
   * @param {HTMLElement} target - The changed element.
   * @returns {HTMLElement | undefined} The effective target.
   */
  #getEffectiveTarget(target) {
    if (
      target instanceof HTMLSelectElement &&
      target.dataset.auxiliarySelect === "true"
    ) {
      const selectedOption = target.options[target.selectedIndex];
      const optionValueId = selectedOption?.dataset.optionValueId;

      if (!optionValueId) return undefined;

      const linkedInput = this.querySelector(
        `input[data-option-value-id="${optionValueId}"]`,
      );

      if (!(linkedInput instanceof HTMLInputElement)) {
        throw new Error("No linked input found for auxiliary select");
      }

      return linkedInput;
    }

    return target;
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === "string") {
      const targetElement = this.querySelector(
        `[data-option-value-id="${target}"]:not([data-auxiliary-option="true"])`,
      );

      if (!targetElement) throw new Error("Target element not found");

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find(
        (option) => option.value === newValue,
      );

      if (!newSelectedOption) throw new Error("Option not found");

      for (const option of target.options) {
        option.removeAttribute("selected");
      }

      newSelectedOption.setAttribute("selected", "selected");
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(
    selectedOption,
    source = null,
    sourceSelectedOptionsValues = [],
  ) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl =
      selectedOption.dataset.connectedProductUrl ||
      this.#pendingRequestUrl ||
      this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(",")}`);
    } else if (source === "product-card") {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(",")}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of quick-add-component or swatches-variant-picker-component, we need to append section_id=section-rendering-product-card to the URL
    if (
      this.closest("quick-add-component") ||
      this.closest("swatches-variant-picker-component")
    ) {
      if (productUrl?.includes("?")) {
        productUrl = productUrl.split("?")[0];
      }
      return `${productUrl}?section_id=section-rendering-product-card&${params.join("&")}`;
    }

    // Preserve the page's alternate template (?view=) so template-gated
    // markup (e.g. A/B test height labels) survives the re-render.
    const view = new URL(window.location.href).searchParams.get("view");
    if (view && !this.closest('[data-purchase-context="embedded"]')) {
      params.push(`view=${encodeURIComponent(view)}`);
    }

    return `${productUrl}?${params.join("&")}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {boolean} shouldMorphMain - If the entire main content should be morphed. By default, only the variant picker is morphed.
   */
  fetchUpdatedSection(requestUrl, shouldMorphMain = false) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, "text/html");
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector("overflow-list[defer]")?.removeAttribute("defer");

        const textContent = html.querySelector(
          `variant-picker script[type="application/json"]`,
        )?.textContent;
        if (!textContent) return;

        if (shouldMorphMain) {
          this.updateMain(html);
        } else {
          const newProduct = this.updateVariantPicker(html);

          // Re-parse the morphed inline variants and resolve the single selected
          // variant so consumers receive { id, available, option1..3 } — not the
          // full variants array, whose .id/.available are undefined (which would
          // wipe the form's variant id and break add-to-cart on the correction pass).
          this.#parseAllVariants();
          const selectedVariant = this.#findCurrentVariant();

          // Never dispatch the array or a null resource here; the optimistic pass
          // already set the correct id, so skipping is safe if we can't resolve it.
          if (this.selectedOptionId && selectedVariant) {
            this.dispatchEvent(
              new VariantUpdateEvent(selectedVariant, this.selectedOptionId, {
                html,
                productId: this.dataset.productId ?? "",
                newProduct,
              }),
            );
          }
        }
      })
      .catch((error) => {
        if (error.name === "AbortError") {
          console.warn("Fetch aborted by user");
        } else {
          console.error(error);
        }
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(
      this.tagName.toLowerCase(),
    );

    if (!newVariantPickerSource) {
      throw new Error("No new variant picker source found");
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (
        newProductId &&
        newProductUrl &&
        this.dataset.productId !== newProductId
      ) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    // Pin height before morph to prevent content below from shifting if the picker shrinks
    const preHeight = this.offsetHeight;
    this.style.minHeight = `${preHeight}px`;
    morph(this, newVariantPickerSource);
    requestAnimationFrame(() => this.style.removeProperty("min-height"));

    return newProduct;
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector("main");
    const newMain = newHtml.querySelector("main");

    if (!main || !newMain) {
      throw new Error("No new main source found");
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector(
      'select:not([data-auxiliary-select="true"]) option[selected], fieldset input:checked',
    );

    if (!(
      selectedOption instanceof HTMLInputElement ||
      selectedOption instanceof HTMLOptionElement
    )) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error("No option value ID found");
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(
      this.querySelectorAll(
        'select:not([data-auxiliary-select="true"]) option[selected], fieldset input:checked',
      ),
    );

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error("No option value ID found");

      return optionValueId;
    });
  }
}

if (!customElements.get("variant-picker")) {
  customElements.define("variant-picker", VariantPicker);
}
